import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Alert, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  useAppContext,
  callNativeMobileApp,
  useTranslator,
} from '@front-activerse/common-components';
import { findCameraWithTorch } from '../helpers';

interface PhotoCaptureContainerProps {
  onPhotoTaken: (file: File) => void;
}

const PhotoCaptureContainer: React.FC<PhotoCaptureContainerProps> = ({ onPhotoTaken }) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [isSearchingCamera, setIsSearchingCamera] = useState(false);

  const { t } = useTranslator();

  const {
    images: {
      cameraShutter,
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      photoGalleryIcon,
      videoPoster,
    },
  } = useAppContext();

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setIsSearchingCamera(true);

      let cameraIdToUse = selectedCameraId;

      if (!cameraIdToUse) {
        try {
          const betterCameraId = await findCameraWithTorch();

          if (betterCameraId) {
            cameraIdToUse = betterCameraId;
            setSelectedCameraId(cameraIdToUse);
          } else {
            const initialConstraints: MediaStreamConstraints = {
              video: {
                facingMode: 'environment',
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
            };

            const initialStream = await navigator.mediaDevices.getUserMedia(initialConstraints);
            const initialTrack = initialStream.getVideoTracks()[0];
            const initialDeviceId = initialTrack.getSettings().deviceId;

            cameraIdToUse = initialDeviceId || '';
            setSelectedCameraId(cameraIdToUse);
          }
        } catch (permissionErr) {
          console.error('Permission denied or camera not available:', permissionErr);
          throw permissionErr;
        }
      }

      if (!cameraIdToUse) {
        setError('No camera available');
        setIsSearchingCamera(false);
        return;
      }

      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: { exact: cameraIdToUse },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities() as MediaTrackCapabilities & {
        torch?: boolean;
      };

      setIsTorchSupported(!!capabilities.torch);
      setIsCameraActive(true);
      setTimeout(() => {
        setIsVideoVisible(true);
      }, 100);
      setIsSearchingCamera(false);
    } catch (err) {
      console.error('Error accessing camera:', err);
      const error = err as Error;
      setError(`Unable to access camera: ${error.message}`);
      setIsCameraActive(false);
      setIsVideoVisible(false);
      setIsSearchingCamera(false);
    }
  }, [selectedCameraId]);

  const stopCamera = useCallback(async () => {
    try {
      if (isTorchOn && streamRef.current) {
        try {
          const videoTrack = streamRef.current.getVideoTracks()[0];
          if (videoTrack) {
            await videoTrack.applyConstraints({
              // @ts-expect-error - torch is not in TypeScript definitions yet
              advanced: [{ torch: false }],
            });
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        } catch (torchErr) {
          console.error('Error turning off torch:', torchErr);
        }
      }

      if (videoRef.current) {
        try {
          videoRef.current.pause();
          videoRef.current.srcObject = null;
        } catch (videoErr) {
          console.error('Error clearing video element:', videoErr);
        }
      }

      if (streamRef.current) {
        try {
          const tracks = streamRef.current.getTracks();
          if (tracks.length > 0) {
            tracks.forEach((track) => track.stop());
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
          streamRef.current = null;
        } catch (err) {
          console.error('Error stopping camera tracks:', err);
          streamRef.current = null;
        }
      }

      setIsCameraActive(false);
      setIsVideoVisible(false);
      setIsTorchOn(false);
    } catch (err) {
      console.error('Error in stopCamera:', err);
    }
  }, [isTorchOn]);

  const toggleTorch = async () => {
    if (!streamRef.current) return;

    try {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      await videoTrack.applyConstraints({
        // @ts-expect-error - torch is not in TypeScript definitions yet
        advanced: [{ torch: !isTorchOn }],
      });
      setIsTorchOn(!isTorchOn);
    } catch (err) {
      console.error('Error toggling torch:', err);
    }
  };

  const cropImageToSquare = (
    sourceImage: HTMLImageElement | HTMLVideoElement,
    canvas: HTMLCanvasElement
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const maxCropSize = 1024;
      const sourceWidth =
        sourceImage instanceof HTMLVideoElement ? sourceImage.videoWidth : sourceImage.width;
      const sourceHeight =
        sourceImage instanceof HTMLVideoElement ? sourceImage.videoHeight : sourceImage.height;

      let scaledWidth = sourceWidth;
      let scaledHeight = sourceHeight;
      const maxDimension = Math.max(sourceWidth, sourceHeight);

      if (maxDimension > maxCropSize) {
        const scale = maxCropSize / maxDimension;
        scaledWidth = Math.round(sourceWidth * scale);
        scaledHeight = Math.round(sourceHeight * scale);
      }

      const cropSize = Math.min(scaledWidth, scaledHeight);
      const centerX = scaledWidth / 2;
      const centerY = scaledHeight / 2;
      const cropX = Math.max(0, centerX - cropSize / 2);
      const cropY = Math.max(0, centerY - cropSize / 2);

      canvas.width = cropSize;
      canvas.height = cropSize;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        if (maxDimension > maxCropSize) {
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = scaledWidth;
          tempCanvas.height = scaledHeight;
          const tempCtx = tempCanvas.getContext('2d');

          if (tempCtx) {
            tempCtx.drawImage(sourceImage, 0, 0, scaledWidth, scaledHeight);
            ctx.drawImage(tempCanvas, cropX, cropY, cropSize, cropSize, 0, 0, cropSize, cropSize);
          }
        } else {
          const sourceCropX = Math.max(0, sourceWidth / 2 - cropSize / 2);
          const sourceCropY = Math.max(0, sourceHeight / 2 - cropSize / 2);
          ctx.drawImage(
            sourceImage,
            sourceCropX,
            sourceCropY,
            cropSize,
            cropSize,
            0,
            0,
            cropSize,
            cropSize
          );
        }

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], 'activity-photo.jpg', { type: 'image/jpeg' });
              resolve(file);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          0.9
        );
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    });
  };

  const handlePhotoReady = async (file: File) => {
    await stopCamera();
    onPhotoTaken(file);
  };

  const handleCapture = async () => {
    if (!videoRef.current || !canvasRef.current || isScanning) return;

    try {
      setIsScanning(true);
      setError(null);
      const video = videoRef.current;
      const canvas = canvasRef.current;

      const file = await cropImageToSquare(video, canvas);
      await handlePhotoReady(file);
    } catch (error) {
      console.error('Error capturing image:', error);
      setError('Error capturing image. Please try again.');
    } finally {
      setIsScanning(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !canvasRef.current || isScanning) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    try {
      setIsScanning(true);
      setError(null);

      const img = new Image();
      const canvas = canvasRef.current;

      img.onload = async () => {
        try {
          const croppedFile = await cropImageToSquare(img, canvas);
          await handlePhotoReady(croppedFile);
        } catch (error) {
          console.error('Error processing uploaded image:', error);
          setError('Error processing image. Please try again.');
          setIsScanning(false);
        } finally {
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };

      img.onerror = () => {
        setError('Failed to load image. Please try another file.');
        setIsScanning(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };

      img.src = URL.createObjectURL(file);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Error uploading file. Please try again.');
      setIsScanning(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    let mounted = true;
    let cleanupCalled = false;

    const initCamera = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (mounted && !cleanupCalled) {
        await startCamera();
      }
    };

    initCamera();

    const handleBeforeUnload = () => {
      stopCamera();
    };

    const handlePageHide = () => {
      stopCamera();
    };

    const handleMessage = (e: MessageEvent) => {
      if (e.data.action === 'release_camera_and_close_modal') {
        handleCloseClick();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('message', handleMessage);

    return () => {
      mounted = false;
      cleanupCalled = true;
      stopCamera();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseClick = async () => {
    await stopCamera();
    await new Promise((resolve) => setTimeout(resolve, 100));

    callNativeMobileApp({
      action: 'close_modal',
    });
    navigate(-1);
  };

  return (
    <div className="scan-plate">
      <video
        ref={videoRef}
        className="camera-video-fullscreen"
        playsInline
        muted
        poster={videoPoster}
        style={{ opacity: isVideoVisible ? 1 : 0 }}
      />

      <div className="camera-overlay-fullscreen">
        {isCameraActive && <div className="scan-area" />}

        <div className="top-controls">
          <button className="scan-plate-button" onClick={handleCloseClick}>
            <img src={closeWhiteIcon} alt="" />
          </button>
        </div>

        <div className="bottom-controls">
          {error ? (
            <div className="text-center">
              <Alert color="danger" className="mb-0" fade={false}>
                <p className="mb-0 text-text-main-color text-start">
                  {t('tracker.scanFood.noAccessCamera.notification')}
                </p>
              </Alert>

              {!isCameraActive && !isSearchingCamera && (
                <Button
                  color="link"
                  size="sm"
                  onClick={() => window.location.reload()}
                  disabled={isSearchingCamera || isScanning}
                >
                  <p>{t('tracker.scan.button.refreshCamera')}</p>
                </Button>
              )}
            </div>
          ) : (
            <>
              {isCameraActive && isTorchSupported ? (
                <button className="scan-plate-button" onClick={toggleTorch} disabled={isScanning}>
                  <img src={isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                </button>
              ) : (
                <button className="scan-plate-button" disabled>
                  <img src={flashOffIcon} alt="" />
                </button>
              )}

              {!isCameraActive && isSearchingCamera && (
                <p>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  {t('tracker.scan.findingCamera')}
                </p>
              )}

              {isCameraActive && !isSearchingCamera && (
                <button className="scan-plate-button" onClick={handleCapture} disabled={isScanning}>
                  <img src={cameraShutter} alt="" />
                </button>
              )}

              <button
                className="scan-plate-button"
                onClick={handleUploadClick}
                disabled={isScanning || isSearchingCamera}
              >
                <img src={photoGalleryIcon} alt="" />
              </button>
            </>
          )}
        </div>
      </div>

      <canvas ref={canvasRef} className="d-none" />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleFileUpload}
      />
    </div>
  );
};

export default PhotoCaptureContainer;
